import { useEffect, useState, useRef } from "react";
import cn from "classnames";

// components
import {
  Button,
  Textarea,
  Input,
} from "components/LibraryComponents/Components";
import CapabilitiesStatement from "./CapabilitiesStatement";
import DisplayValue from "components/BusinessProfileSections/DisplayValue";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// types
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";

// utils
import { saveErrorToast } from "utils/edit-profile";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// fetch
import { updateExtendedProfileData } from "data/fetch";

// styles
import styles from "./OverviewSection.module.scss";

const fieldToConfirmationMap = {
  capabilities_narrative: "Capabilities narrative" as const,
  capabilities_statement_link: "Capabilities statement link" as const,
  additional_website: "Additional website" as const,
}

const MAX_CAPABILITIES_CHARACTERS = 1000;

export default function OverviewSection({
  setUnsavedChanges,
  businessProfileData,
}: SectionsProps) {
  // edit profile data store
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  // local states for input fields and tracking changes
  const [formState, setFormState] = useState({
    capabilities_narrative: "",
    capabilities_statement_link: "",
    additional_website: "",
  });
  const [showCapabilityLimitError, setShowCapabilityLimitError] =
    useState<boolean>(false);
  const [savingFields, setSavingFields] = useState<{
    [key: string]: boolean;
  }>({}); // Tracks saving state per field

  type ConfirmationSection = typeof fieldToConfirmationMap.capabilities_narrative | typeof fieldToConfirmationMap.capabilities_statement_link | typeof fieldToConfirmationMap.additional_website;
  const {
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = useSaveConfirmation<ConfirmationSection>();

  const addTask = useMeiliTaskToast();

  const isInitialized = useRef(false); // Track if formState has been initialized
  const formRef = useRef<HTMLFormElement>(null);

  // Track changes per field
  const hasFieldChanged = (field: keyof typeof formState) => {
    if (!formState?.[field] && !extendedProfile?.[field]) { // allow comparing "" and null to be considered unchanged
      return false
    } else return formState[field] !== extendedProfile?.[field];
  }
  
  // Track if any field has changed for setUnsavedChanges
  const hasAnyChanges = Object.keys(formState).some((key) =>
    hasFieldChanged(key as keyof typeof formState)
  );

  const isFormStateEmpty = Object.values(formState).every(
    (value) => value === ""
  );

  useEffect(() => {
    setUnsavedChanges(hasAnyChanges);
    clearConfirmation();
    return () => {
      setUnsavedChanges(false);
    };
  }, [hasAnyChanges, setUnsavedChanges]);

  // Initialize formState only when extendedProfile is available and formState is empty
  useEffect(() => {
    if (extendedProfile && !isInitialized.current && isFormStateEmpty) {
      setFormState({
        capabilities_narrative: extendedProfile.capabilities_narrative ?? "",
        capabilities_statement_link:
          extendedProfile.capabilities_statement_link ?? "",
        additional_website: extendedProfile.additional_website ?? "",
      });

      isInitialized.current = true; // Mark as initialized to prevent future updates
    }
  }, [extendedProfile]);

  const handleFieldChange = (
    field: keyof typeof formState,
    value: string,
    maxLength?: number
  ) => {
    if (maxLength && value.length > maxLength) {
      if (field === "capabilities_narrative") {
        setShowCapabilityLimitError(true);
      }
      return;
    }

    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "capabilities_narrative" && showCapabilityLimitError) {
      setShowCapabilityLimitError(false);
    }
  };

  const handleSaveField = async (field: keyof typeof formState) => {
    if (!hasFieldChanged(field) || savingFields[field]) return;

    if (!formRef.current?.reportValidity()) {
      return;
    }

    setSavingFields((prev) => ({ ...prev, [field]: true }));

    try {
      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          [field]: formState[field],
        })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 2000));

      setExtendedProfile({
        ...extendedProfile,
        [field]: formState[field],
      });

      const responseData = await response.json();
      const confirmationLabel = fieldToConfirmationMap[field] as typeof fieldToConfirmationMap.capabilities_narrative | typeof fieldToConfirmationMap.capabilities_statement_link | typeof fieldToConfirmationMap.additional_website;
      addTask({
        taskUid: responseData?.taskUid,
        label: confirmationLabel,
        status: responseData?.status,
      });
      showConfirmation(confirmationLabel);
    } catch (error) {
      console.error(`Error saving ${field}:`, error);
      saveErrorToast();
    } finally {
      setSavingFields((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <div className={styles["overview-section-root"]}>
      <form ref={formRef}>
        <h2 className={styles["heading"]}>Overview information</h2>
        {/* Capabilities narrative */}
        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>Capabilities narrative</h3>
            <p className={styles["desc"]}>
              Describe what your small business does in a short paragraph.
            </p>
          </div>

          <div className={styles["text-area-wrapper"]}>
            <Textarea
              id="narrative"
              name="narrative"
              label="Capabilities narrative"
              placeholder="Type your capabilities narrative here"
              value={formState.capabilities_narrative}
              onChange={(e) =>
                handleFieldChange(
                  "capabilities_narrative",
                  e.target.value,
                  MAX_CAPABILITIES_CHARACTERS
                )
              }
              inputClassName={
                showCapabilityLimitError ? styles["cn-limit-error"] : ""
              }
            />

            <span className={styles["cn-limit-text"]}>
              {formState.capabilities_narrative.length}/
              {MAX_CAPABILITIES_CHARACTERS}
            </span>

            {showCapabilityLimitError && (
              <span className={styles["cn-limit-error"]}>
                Capabilities narrative must be under 1000 characters.
              </span>
            )}
          </div>

          <ResponsiveStack>
            <Button
              type="button"
              loading={savingFields.capabilities_narrative}
              disabled={
                !hasFieldChanged("capabilities_narrative") ||
                savingFields.capabilities_narrative
              }
              onClick={() => handleSaveField("capabilities_narrative")}
            >
              {savingFields.capabilities_narrative ? "Saving..." : "Save changes"}
            </Button>
              {lastSavedSection === fieldToConfirmationMap.capabilities_narrative && (
                <Saved />
              )}
          </ResponsiveStack>
        </div>

        {/* Capabilities statement */}
        <CapabilitiesStatement
          uei={extendedProfile?.uei}
          statementLink={formState.capabilities_statement_link}
          isSaveBtnLoading={savingFields.capabilities_statement_link}
          isSaveBtnDisabled={
            !hasFieldChanged("capabilities_statement_link") ||
            savingFields.capabilities_statement_link
          }
          handleChange={(value) => {
            handleFieldChange("capabilities_statement_link", value);
          }}
          handleSave={() => {
            handleSaveField("capabilities_statement_link");
          }}
          lastSavedSection={lastSavedSection}
          fieldToConfirmationMap={fieldToConfirmationMap}
          clearConfirmation={clearConfirmation}
        />

        {/* <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>Capabilities Statement Link</h3>
            <p className={styles["desc"]}>
              Paste a url to a webpage or publicly available pdf upload to your
              capabilities statement.
            </p>
          </div>

          <Input
            id="capabilities"
            name="capabilities"
            type="text"
            label="Capabilities Statement Link"
            placeholder="https://www.example.com"
            value={formState.capabilities_statement_link}
            onChange={(e) =>
              handleFieldChange("capabilities_statement_link", e.target.value)
            }
          />

          <Button
            type="button"
            loading={savingFields.capabilities_statement_link}
            disabled={
              !hasFieldChanged("capabilities_statement_link") ||
              savingFields.capabilities_statement_link
            }
            onClick={() => handleSaveField("capabilities_statement_link")}
          >
            {savingFields.capabilities_statement_link
              ? "Saving..."
              : "Save changes"}
          </Button>
        </div> */}

        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>Additional website</h3>
            <p className={styles["desc"]}>
              The SBA allows you to link to an additional website on Small
              Business Search, formerly labeled “E-Commerce Website.”
            </p>
          </div>

          <Input
            id="website"
            name="website"
            type="text"
            label="Additional website"
            placeholder="https://www.example.com"
            value={formState.additional_website}
            onChange={(e) =>
              handleFieldChange("additional_website", e.target.value)
            }
          />

          <ResponsiveStack>
            <Button
              type="button"
              loading={savingFields.additional_website}
              disabled={
                !hasFieldChanged("additional_website") ||
                savingFields.additional_website
              }
              onClick={() => handleSaveField("additional_website")}
            >
              {savingFields.additional_websites ? "Saving..." : "Save changes"}
            </Button>
            {lastSavedSection === fieldToConfirmationMap.additional_website && <Saved />}
          </ResponsiveStack>
        </div>

        {/* Information from SAM.gov */}
        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>Information from SAM.gov</h3>
            <p className={styles["desc"]}>
              The following items come from this small business’s SAM.gov
              registration, and must be updated there.
              <a
                href="https://www.sam.gov/"
                target="_blank"
                rel="noreferrer"
                className="usa-link usa-link--external"
              >
                Log in to SAM.gov
              </a>
            </p>
          </div>

          <div className={styles["space-y-4"]}>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Business name</p>
              <DisplayValue
                value={businessProfileData?.legal_business_name}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>
                Trade name (“Doing business as...”)
              </p>
              <DisplayValue
                value={businessProfileData?.dba_name}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Address</p>
              {businessProfileData?.address_1 && (
                <div>
                  <DisplayValue
                    value={businessProfileData?.address_1}
                    className={styles["value"]}
                    message=""
                  />
                </div>
              )}
              {businessProfileData?.address_2 && (
                <div>
                  <DisplayValue
                    value={businessProfileData?.address_2}
                    className={styles["value"]}
                  />
                </div>
              )}
              <DisplayValue
                value={`${businessProfileData?.city}, ${businessProfileData?.state} ${businessProfileData?.zipcode}`}
                className={styles["value"]}
              />
              {/* <p className={styles["value"]}>Address line 1</p>
            <p className={styles["value"]}>Address line 2</p>
            <p className={styles["value"]}>City, State Zip</p> */}
            </div>
            <p className={styles["info-text"]}>
              County code, congressional district, and metropolitan statistical
              area are calculated based on address, and will update if the
              address is updated in SAM.gov.
            </p>
            <div className={cn(styles["group"], styles["ml-6"])}>
              <p className={styles["label"]}>County code</p>
              <DisplayValue
                value={businessProfileData?.county_code}
                className={styles["value"]}
              />
            </div>
            <div className={cn(styles["group"], styles["ml-6"])}>
              <p className={styles["label"]}>Congressional District</p>
              <DisplayValue
                value={businessProfileData?.congressional_district}
                className={styles["value"]}
              />
            </div>
            <div className={cn(styles["group"], styles["ml-6"])}>
              <p className={styles["label"]}>Metropolitan Statistical Area</p>
              <DisplayValue
                value={businessProfileData?.msa}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Phone number</p>
              <DisplayValue
                value={businessProfileData?.phone}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Fax number</p>
              <DisplayValue
                value={businessProfileData?.fax}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Email address</p>
              <DisplayValue
                value={businessProfileData?.email}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Contact person</p>
              <DisplayValue
                value={businessProfileData?.contact_person}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>CAGE Code</p>
              <DisplayValue
                value={businessProfileData?.cage_code}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Website</p>
              <DisplayValue
                value={businessProfileData?.website}
                className={styles["value"]}
              />
            </div>
            <div className={styles["group"]}>
              <p className={styles["label"]}>Year established</p>
              <DisplayValue
                value={businessProfileData?.year_established}
                className={styles["value"]}
              />
            </div>
            <p className={styles["info-text"]}>
              Number of employees and average gross revenue are only visible to
              logged-in federal employee users.
            </p>
            <div className={cn(styles["group"], styles["ml-6"])}>
              <p className={styles["label"]}>Number of employees</p>
              <DisplayValue
                value={businessProfileData?.business_size}
                className={styles["value"]}
              />
            </div>
            <div className={cn(styles["group"], styles["ml-6"])}>
              <p className={styles["label"]}>Annual gross revenue</p>
              <DisplayValue
                value={businessProfileData?.annual_revenue}
                className={styles["value"]}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
